import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  DocumentIcon,
  PhotoIcon,
  VideoCameraIcon,
  ArrowDownTrayIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  UserIcon,
  CalendarIcon,
  TagIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  LockClosedIcon,
  GlobeAltIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { resourcesAPI } from '../../services/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { useToast } from '../../context/ToastContext';

interface Resource {
  _id: string;
  fileName: string;
  originalName: string;
  fileType?: string;
  mimeType: string;
  fileSize: number;
  fileSizeFormatted: string;
  uploadedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  relatedTo?: {
    objectType: string;
    objectId: string;
  };
  category: string;
  description?: string;
  tags: string[];
  isConfidential: boolean;
  accessLevel: string;
  expirationDate?: string;
  version: number;
  previousVersions: Array<{
    versionNumber: number;
    fileName: string;
    uploadedBy?: {
      firstName: string;
      lastName: string;
    };
    uploadedAt: string;
    changeLog?: string;
  }>;
  downloadCount: number;
  lastDownloaded?: string;
  isActive: boolean;
  isExpired?: boolean;
  daysUntilExpiration?: number;
  isImage: boolean;
  isDocument: boolean;
  isVideo: boolean;
  metadata?: {
    width?: number;
    height?: number;
    duration?: number;
    pages?: number;
    author?: string;
    subject?: string;
    keywords?: string[];
  };
  createdAt: string;
  updatedAt: string;
}

const ResourceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success: showSuccess, error: showError } = useToast();

  // Fetch resource details
  const {
    data: resourceData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['resource', id],
    queryFn: async () => {
      console.log('Fetching resource with ID:', id);
      try {
        const result = await resourcesAPI.getResource(id!);
        console.log('Resource API response:', result);
        return result;
      } catch (err) {
        console.error('Resource API error:', err);
        console.error('Error response:', (err as any)?.response);
        console.error('Error config:', (err as any)?.config);
        throw err;
      }
    },
    enabled: !!id,
  });

  const resource: Resource | null = resourceData?.data?.data?.resource || null;

  // Delete resource mutation
  const deleteResourceMutation = useMutation({
    mutationFn: () => resourcesAPI.deleteResource(id!),
    onSuccess: () => {
      showSuccess('Resource deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['resources'] });
      navigate('/resources');
    },
    onError: (error: any) => {
      showError(error.response?.data?.error || 'Failed to delete resource');
    },
  });

  // Handle download with authentication
  const handleDownload = async () => {
    try {
      const response = await resourcesAPI.downloadResource(id!);
      
      // Create blob and download link
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = resource?.originalName || resource?.fileName || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      showError(error.response?.data?.message || 'Failed to download resource');
    }
  };

  const handleDeleteResource = async () => {
    if (resource && window.confirm(`Are you sure you want to delete "${resource.originalName}"?`)) {
      deleteResourceMutation.mutate();
    }
  };

  const getFileIcon = (resource: Resource) => {
    if (resource.isImage) {
      return <PhotoIcon className="w-8 h-8 text-green-600" />;
    } else if (resource.isVideo) {
      return <VideoCameraIcon className="w-8 h-8 text-blue-600" />;
    } else {
      return <DocumentIcon className="w-8 h-8 text-gray-600" />;
    }
  };

  const getStatusBadge = (resource: Resource) => {
    if (resource.isExpired) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 text-red-800">
          <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
          Expired
        </span>
      );
    } else if (resource.daysUntilExpiration && resource.daysUntilExpiration <= 30) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800">
          <ClockIcon className="w-4 h-4 mr-1" />
          Expires in {resource.daysUntilExpiration} days
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
          <CheckCircleIcon className="w-4 h-4 mr-1" />
          Active
        </span>
      );
    }
  };

  const getAccessLevelIcon = (accessLevel: string) => {
    switch (accessLevel) {
      case 'public':
        return <GlobeAltIcon className="w-4 h-4" />;
      case 'restricted':
        return <LockClosedIcon className="w-4 h-4" />;
      default:
        return <ShieldCheckIcon className="w-4 h-4" />;
    }
  };

  const getAccessLevelBadge = (accessLevel?: string) => {
    const colors = {
      public: 'bg-blue-100 text-blue-800',
      internal: 'bg-gray-100 text-gray-800',
      restricted: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${colors[accessLevel as keyof typeof colors]}`}>
        {getAccessLevelIcon(accessLevel || 'public')}
        <span className="ml-1">{accessLevel ? accessLevel.charAt(0).toUpperCase() + accessLevel.slice(1) : 'Unknown'}</span>
      </span>
    );
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error || !resource) {
    const axiosError = error as any;
    const errorMessage = axiosError?.response?.data?.message || axiosError?.message || 'Unknown error';
    const statusCode = axiosError?.response?.status;
    
    console.log('ResourceDetail error details:', {
      error,
      axiosError,
      errorMessage,
      statusCode,
      response: axiosError?.response,
      resourceData
    });
    
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">Error loading resource details.</p>
          <p className="text-red-500 text-sm mt-2">
            {statusCode && `Status ${statusCode}: `}{errorMessage}
          </p>
          <p className="text-gray-500 text-sm mt-1">
            Resource ID: {id}
          </p>
          {process.env.NODE_ENV === 'development' && (
            <details className="mt-3">
              <summary className="text-sm text-gray-600 cursor-pointer">Debug Info</summary>
              <pre className="text-xs text-gray-600 mt-2 bg-gray-100 p-2 rounded overflow-auto max-h-40">
                {JSON.stringify({
                  error: error?.toString(),
                  statusCode,
                  errorMessage,
                  responseData: axiosError?.response?.data,
                  resourceData
                }, null, 2)}
              </pre>
            </details>
          )}
          <button
            onClick={() => navigate('/resources')}
            className="mt-3 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Back to Resources
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/resources"
              className="inline-flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to Documents
            </Link>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleDownload}
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
              Download
            </button>
            <Link
              to={`/resources/${resource._id}/edit`}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <PencilIcon className="w-4 h-4 mr-2" />
              Edit
            </Link>
            <button
              onClick={handleDeleteResource}
              className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              <TrashIcon className="w-4 h-4 mr-2" />
              Delete
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Document Overview */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-start space-x-4">
              {getFileIcon(resource)}
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  {resource.originalName}
                </h1>
                <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                  <span>{resource.fileType?.toUpperCase() || 'Unknown'}</span>
                  <span>•</span>
                  <span>{resource.fileSizeFormatted}</span>
                  <span>•</span>
                  <span>Version {resource.version}</span>
                  {resource.downloadCount > 0 && (
                    <>
                      <span>•</span>
                      <span>{resource.downloadCount} downloads</span>
                    </>
                  )}
                </div>
                <div className="flex items-center space-x-3 mb-4">
                  {getStatusBadge(resource)}
                  {getAccessLevelBadge(resource.accessLevel)}
                  {resource.isConfidential && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-orange-100 text-orange-800">
                      <LockClosedIcon className="w-4 h-4 mr-1" />
                      Confidential
                    </span>
                  )}
                </div>
                {resource.description && (
                  <p className="text-gray-700">{resource.description}</p>
                )}
              </div>
            </div>

            {/* Tags */}
            {resource.tags && resource.tags.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center mb-2">
                  <TagIcon className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-700">Tags</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {resource.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Metadata */}
            {resource.metadata && Object.keys(resource.metadata).length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 mb-3">File Properties</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {resource.metadata.width && resource.metadata.height && (
                    <div>
                      <span className="text-gray-500">Dimensions:</span>
                      <span className="ml-2 text-gray-900">
                        {resource.metadata.width} × {resource.metadata.height}
                      </span>
                    </div>
                  )}
                  {resource.metadata.pages && (
                    <div>
                      <span className="text-gray-500">Pages:</span>
                      <span className="ml-2 text-gray-900">{resource.metadata.pages}</span>
                    </div>
                  )}
                  {resource.metadata.duration && (
                    <div>
                      <span className="text-gray-500">Duration:</span>
                      <span className="ml-2 text-gray-900">
                        {Math.floor(resource.metadata.duration / 60)}:{(resource.metadata.duration % 60).toString().padStart(2, '0')}
                      </span>
                    </div>
                  )}
                  {resource.metadata.author && (
                    <div>
                      <span className="text-gray-500">Author:</span>
                      <span className="ml-2 text-gray-900">{resource.metadata.author}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Version History */}
          {resource.previousVersions && resource.previousVersions.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Version History</h3>
              <div className="space-y-4">
                {/* Current Version */}
                <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900">
                      Version {resource.version} (Current)
                    </div>
                    <div className="text-sm text-gray-600">
                      Uploaded by {resource.uploadedBy?.firstName || 'Unknown'} {resource.uploadedBy?.lastName || ''} on{' '}
                      {new Date(resource.updatedAt).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={handleDownload}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    <ArrowDownTrayIcon className="w-4 h-4" />
                  </button>
                </div>

                {/* Previous Versions */}
                {resource.previousVersions
                  .sort((a, b) => b.versionNumber - a.versionNumber)
                  .map((version) => (
                    <div key={version.versionNumber} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">
                          Version {version.versionNumber}
                        </div>
                        <div className="text-sm text-gray-600">
                          Uploaded by {version.uploadedBy?.firstName || 'Unknown'} {version.uploadedBy?.lastName || ''} on{' '}
                          {new Date(version.uploadedAt).toLocaleDateString()}
                        </div>
                        {version.changeLog && (
                          <div className="text-sm text-gray-500 mt-1">
                            {version.changeLog}
                          </div>
                        )}
                      </div>
                      <div className="text-sm text-gray-400">
                        Previous Version
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Key Details */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Details</h3>
            <div className="space-y-4">
              <div>
                <span className="text-sm text-gray-500">Category</span>
                <div className="font-medium text-gray-900">{resource.category}</div>
              </div>

              <div>
                <span className="text-sm text-gray-500">Related To</span>
                <div className="font-medium text-gray-900">
                  {resource.relatedTo?.objectType || 'Not specified'}
                </div>
              </div>

              <div>
                <span className="text-sm text-gray-500">Uploaded By</span>
                <div className="flex items-center mt-1">
                  <UserIcon className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="font-medium text-gray-900">
                    {resource.uploadedBy?.firstName || 'Unknown'} {resource.uploadedBy?.lastName || ''}
                  </span>
                </div>
              </div>

              <div>
                <span className="text-sm text-gray-500">Upload Date</span>
                <div className="flex items-center mt-1">
                  <CalendarIcon className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="font-medium text-gray-900">
                    {new Date(resource.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>

              {resource.expirationDate && (
                <div>
                  <span className="text-sm text-gray-500">Expiration Date</span>
                  <div className="flex items-center mt-1">
                    <CalendarIcon className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="font-medium text-gray-900">
                      {new Date(resource.expirationDate).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              )}

              {resource.lastDownloaded && (
                <div>
                  <span className="text-sm text-gray-500">Last Downloaded</span>
                  <div className="flex items-center mt-1">
                    <ArrowDownTrayIcon className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="font-medium text-gray-900">
                      {new Date(resource.lastDownloaded).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button
                onClick={handleDownload}
                className="flex items-center w-full px-4 py-2 text-left text-sm text-green-700 bg-green-50 rounded-lg hover:bg-green-100"
              >
                <ArrowDownTrayIcon className="w-4 h-4 mr-3" />
                Download File
              </button>
              
              <Link
                to={`/resources/${resource._id}/edit`}
                className="flex items-center w-full px-4 py-2 text-left text-sm text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100"
              >
                <PencilIcon className="w-4 h-4 mr-3" />
                Edit Details
              </Link>

              <button
                onClick={handleDeleteResource}
                className="flex items-center w-full px-4 py-2 text-left text-sm text-red-700 bg-red-50 rounded-lg hover:bg-red-100"
              >
                <TrashIcon className="w-4 h-4 mr-3" />
                Delete Resource
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceDetail;
